/**
 * SurveyEditor Page
 *
 * Page component for editing existing surveys.
 * Loads survey data and passes it to FormBuilder for editing.
 */

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import FormBuilder from './components/FormBuilder'
import * as surveysApi from './api/surveys'
import './App.css'

export default function SurveyEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [survey, setSurvey] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [showSavedBanner, setShowSavedBanner] = useState(false)

  // Load survey data
  useEffect(() => {
    async function loadSurvey() {
      try {
        setLoading(true)
        setError(null)
        const data = await surveysApi.getSurvey(id)
        setSurvey(data)
      } catch (e) {
        console.error('Failed to load survey:', e)
        setError(e.message || 'Failed to load survey')
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      loadSurvey()
    }
  }, [id])

  const handleSave = async (form) => {
    if (!form.title) {
      alert('Please enter a survey title')
      return
    }

    if (!form.questions || form.questions.length === 0) {
      alert('Please add at least one question')
      return
    }

    setSaving(true)
    try {
      // Update survey with the edited form
      await surveysApi.updateSurvey(id, {
        title: form.title,
        definition: form,
        default_language: form.default_language,
        text_direction: form.text_direction,
        available_languages: form.available_languages,
        allowed_response_types: form.allowed_response_types,
      })

      setShowSavedBanner(true)
      setTimeout(() => setShowSavedBanner(false), 3000)
    } catch (e) {
      console.error('Failed to save survey:', e)
      alert(e.message || 'Failed to save survey')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="surveybuilder-page">
        <header className="app-header">
          <div className="container">
            <h1>Edit Survey</h1>
            <p>Loading survey...</p>
          </div>
        </header>
        <main className="survey-main">
          <div className="container" style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="spinner">Loading...</div>
          </div>
        </main>
      </div>
    )
  }

  if (error) {
    return (
      <div className="surveybuilder-page">
        <header className="app-header">
          <div className="container">
            <h1>Edit Survey</h1>
            <p>Error loading survey</p>
          </div>
        </header>
        <main className="survey-main">
          <div className="container" style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ color: '#dc3545' }}>{error}</p>
            <button
              className="btn"
              onClick={() => navigate('/dashboard')}
              style={{ marginTop: '1rem' }}>
              Back to Dashboard
            </button>
          </div>
        </main>
      </div>
    )
  }

  // The survey definition contains the form data (title, description, questions, etc.)
  const initialForm = survey?.definition || {
    title: survey?.title || '',
    description: '',
    questions: [],
    default_language: survey?.default_language || 'en',
    text_direction: survey?.text_direction || 'ltr',
    available_languages: survey?.available_languages || ['en'],
    allowed_response_types: survey?.allowed_response_types || ['text'],
  }

  // Brand image: survey-specific theme overrides organization logo
  const brandImageUrl = survey?.theme?.brand_image_url || survey?.organization_logo_url || null

  return (
    <div className="surveybuilder-page">
      <header className="app-header">
        <div className="container">
          <h1>Edit Survey</h1>
          <p>Make changes to your survey below.</p>
        </div>
      </header>
      <main className="survey-main">
        <div className="container" id="survey-wrapper">
          {showSavedBanner && (
            <div className="notice success" style={{ marginBottom: '0.75rem' }}>
              <span>✓ Survey saved successfully!</span>
              <div style={{ display: 'flex', gap: '.5rem' }}>
                <button className="btn" onClick={() => navigate(`/survey/${id}/settings`)}>
                  Go to Settings
                </button>
                <button className="close-btn" onClick={() => setShowSavedBanner(false)}>
                  ✕
                </button>
              </div>
            </div>
          )}
          <FormBuilder
            initialForm={initialForm}
            onSave={handleSave}
            saving={saving}
            isEditing={true}
            brandImageUrl={brandImageUrl}
            surveyId={id}
          />
        </div>
      </main>
    </div>
  )
}
